// frontend/src/pages/Account/AccountProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { getAccountProfile, updateAccountProfile } from '../../services/accountService';
import { updateMyUsername, changeMyPassword } from '../../services/userService'; // TODO: Uncomment when userService is ready
import FormInput from '../../components/FormInput/FormInput';
import Button from '../../components/Button/Button';
import pageStyles from './AccountPage.module.css'; // Common styles for account pages
import styles from './AccountProfilePage.module.css'; // Specific styles for this page
import { useAuth } from '../../contexts/AuthContext';
import AccordionItem from '../../components/AccordionItem/AccordionItem'; // Adjust path if necessary

const AccountProfilePage = () => {
    // --- Auth Context ---
    const { currentUser, checkAuthStatus } = useAuth();

    // --- Form Data State (Main Profile) ---
    const [accountName, setAccountName] = useState('');
    const [primaryContactFullName, setPrimaryContactFullName] = useState('');
    const [primaryContactPhoneNumber, setPrimaryContactPhoneNumber] = useState('');
    const [primaryContactAddress, setPrimaryContactAddress] = useState('');
    const [primaryContactEmail, setPrimaryContactEmail] = useState('');
    const [responsiblePersons, setResponsiblePersons] = useState([]);

    // --- State for "User Login Details" Section ---
    // Username Change
    const [editableUsername, setEditableUsername] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [usernameSuccess, setUsernameSuccess] = useState('');
    const [usernameLoading, setUsernameLoading] = useState(false);

    // Password Change (NEW for Task 2.3.3)
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordChangeError, setPasswordChangeError] = useState('');
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');
    const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);

    // UI State (Main Profile)
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const [activeAccordion, setActiveAccordion] = useState(null);

    const [addPersonLoading, setAddPersonLoading] = useState(false);        // NEW
    const [removePersonLoading, setRemovePersonLoading] = useState(false);  // NEW


    // State for managing a new responsible person entry
    const [newResponsiblePerson, setNewResponsiblePerson] = useState({
        fullName: '',
        phoneNumber: '',
        email: '',
        address: ''
    });

    // --- FETCH DATA & INITIALIZE USERNAME EFFECT ---
    useEffect(() => {
        const fetchProfile = async () => {
            setError(null);
            try {
                const data = await getAccountProfile();
                setAccountName(data.accountName || '');
                setPrimaryContactFullName(data.primaryContactFullName || '');
                setPrimaryContactPhoneNumber(data.primaryContactPhoneNumber || '');
                setPrimaryContactAddress(data.primaryContactAddress || '');
                setPrimaryContactEmail(data.primaryContactEmail || '');
                setResponsiblePersons(Array.isArray(data.responsiblePersons) ? data.responsiblePersons : []);
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Could not load account profile.');
                // Clear fields on error
                setAccountName('');
                setPrimaryContactFullName('');
                setPrimaryContactPhoneNumber('');
                setPrimaryContactAddress('');
                setPrimaryContactEmail('');
                setResponsiblePersons([]);
            } finally {
            }
        };

        fetchProfile();

        if (currentUser) {
            setEditableUsername(currentUser.username || '');
        }
    }, [currentUser]);

    useEffect(() => {
        // If the currently‑open panel is NOT "addResponsible", clear the temp object
        if (activeAccordion !== 'addResponsible') {
            setNewResponsiblePerson({
                fullName: '',
                phoneNumber: '',
                email: '',
                address: '',
            });
        }
    }, [activeAccordion]);

    // --- RESPONSIBLE PERSONS HANDLERS ---
    const handleAddResponsiblePerson = async () => {
        if (!newResponsiblePerson.fullName.trim()) {
            setError('Please enter at least a full name for the responsible person.');
            setTimeout(() => setError(null), 3000);
            return;
        }

        const updatedList = [...responsiblePersons, { ...newResponsiblePerson }];

        setAddPersonLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            // persist only the list; backend keeps other fields unchanged
            const resp = await updateAccountProfile({ responsiblePersons: updatedList });

            // take what the server returns (safer) or fall back
            const fresh = resp.account?.responsiblePersons ?? updatedList;
            setResponsiblePersons(fresh);

            setSuccessMessage('Responsible person added successfully!'); // NEW message
            setNewResponsiblePerson({ fullName: '', phoneNumber: '', email: '', address: '' });
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to add responsible person.');
        } finally {
            setAddPersonLoading(false);
            setTimeout(() => setSuccessMessage(''), 4000);
        }
    };

    const handleRemoveResponsiblePerson = async (indexToRemove) => {
        const updatedList = responsiblePersons.filter((_, idx) => idx !== indexToRemove);

        setRemovePersonLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            const resp = await updateAccountProfile({ responsiblePersons: updatedList });

            const fresh = resp.account?.responsiblePersons ?? updatedList;
            setResponsiblePersons(fresh);

            setSuccessMessage('Responsible person removed successfully!'); // NEW message
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to remove responsible person.');
        } finally {
            setRemovePersonLoading(false);
            setTimeout(() => setSuccessMessage(''), 4000);
        }
    };

    // --- USERNAME CHANGE HANDLER ---
    const handleUsernameSave = async () => {
        if (!editableUsername.trim()) {
            setUsernameError('Username cannot be empty.');
            return;
        }
        if (currentUser && editableUsername.trim() === currentUser.username) {
            // Button should be disabled if no change, or provide a message
            return;
        }

        setUsernameLoading(true);
        setUsernameError('');
        setUsernameSuccess('');

        try {
            // The 'updateMyUsername' service function calls the backend.
            // The backend /api/users/me/username endpoint now re-issues the JWT cookie
            // and returns the updated user object.
            const response = await updateMyUsername({ newUsername: editableUsername.trim() });

            setUsernameSuccess(response.message || 'Username updated successfully!');

            // Since the JWT cookie has been updated by the browser (from the backend response),
            // calling checkAuthStatus() will make a request to /api/auth/status with the new cookie.
            // The backend will validate this new cookie and return the updated user details,
            // which AuthContext will then use to update its internal currentUser state.
            if (checkAuthStatus) {
                await checkAuthStatus();
            }
            // After checkAuthStatus completes, the `currentUser` object from `useAuth()` will be updated,
            // and your useEffect hook that depends on `currentUser` (the one that sets `editableUsername`)
            // should re-run, updating the `editableUsername` field in the UI if needed.

        } catch (err) {
            const message = err.message || err.response?.data?.message || 'Failed to update username.';
            setUsernameError(message);
            console.error('Error updating username:', err);
        } finally {
            setUsernameLoading(false);
        }
    };

    // --- PASSWORD CHANGE HANDLER (NEW for Task 2.3.3) ---
    const handlePasswordSave = async () => {
        setPasswordChangeError('');
        setPasswordChangeSuccess('');

        if (!currentPassword || !newPassword || !confirmNewPassword) {
            setPasswordChangeError('All password fields are required.');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setPasswordChangeError('New password and confirm password do not match.');
            return;
        }
        if (newPassword.length < 6) { // Example: basic length validation
            setPasswordChangeError('New password must be at least 6 characters long.');
            return;
        }

        setPasswordChangeLoading(true);

        try {
            const response = await changeMyPassword({ currentPassword, newPassword }); // Actual API call

            setPasswordChangeSuccess(response.message || 'Password changed successfully!');
            setCurrentPassword(''); // Clear fields on success
            setNewPassword('');
            setConfirmNewPassword('');

        } catch (err) {
            const message = err.message || err.response?.data?.message || 'Failed to change password.';
            setPasswordChangeError(message);
            console.error('Error changing password:', err);
        } finally {
            setPasswordChangeLoading(false);
        }
    };

    const handleSavePrimaryContactInfo = async () => {
        setError(null);
        setSuccessMessage('');

        const emailToSend = primaryContactEmail.trim() === '' ? null : primaryContactEmail;
        const profileData = {
            // Only include fields relevant to primary contact and responsible persons
            primaryContactFullName,
            primaryContactPhoneNumber,
            primaryContactAddress,
            primaryContactEmail: emailToSend,
            responsiblePersons // Send the current list of responsible persons
        };

        try {
            const response = await updateAccountProfile(profileData);
            setSuccessMessage(response.message || 'Primary contact information updated successfully!');
            if (response.account) {
                // Update state with potentially sanitized/refetched data from backend
                setPrimaryContactFullName(response.account.primaryContactFullName || '');
                setPrimaryContactPhoneNumber(response.account.primaryContactPhoneNumber || '');
                setPrimaryContactAddress(response.account.primaryContactAddress || '');
                setPrimaryContactEmail(response.account.primaryContactEmail || '');
                setResponsiblePersons(Array.isArray(response.account.responsiblePersons) ? response.account.responsiblePersons : []);
            }
            setActiveAccordion(null); // Optionally close accordion on success
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to update primary contact information.');
            setTimeout(() => setError(null), 5000);
        } finally {
        }
    };

    // --- SAVE ACCOUNT NAME HANDLER ---
    const handleSaveAccountName = async () => {
        setError(null);
        setSuccessMessage('');

        const profileData = {
            accountName, // Only include accountName and responsible persons
            responsiblePersons // Send the current list of responsible persons
        };

        try {
            const response = await updateAccountProfile(profileData);
            setSuccessMessage(response.message || 'Account name updated successfully!');
            if (response.account) {
                // Update state with potentially sanitized/refetched data from backend
                setAccountName(response.account.accountName || '');
                setResponsiblePersons(Array.isArray(response.account.responsiblePersons) ? response.account.responsiblePersons : []);
            }
            setActiveAccordion(null); // Optionally close accordion on success
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to update account name.');
            setTimeout(() => setError(null), 5000);
        } finally {
        }
    };

    // The rest of your component (the return statement with JSX) will go below.

    return (
        <div className={pageStyles.accountPageContainer}>
            <h2 className="page-main-title">Account Profile</h2>
            {error && <div className={pageStyles.errorMessage}>{error}</div>}
            {successMessage && <div className={pageStyles.successMessage}>{successMessage}</div>}

            {/* --- Top Static Display Section --- */}
            <div className={styles.profileHeaderDisplay}>
                <p><strong>Account Name:</strong> {accountName || 'N/A'}</p>
                <p><strong>Username:</strong> {currentUser?.username || 'N/A'}</p>
            </div>

            {/* --- Static Primary Contact Info Section --- */}
            <div className={styles.staticContactInfoSection}>
                <h3 className={styles.sectionTitle}>Primary Contact Information</h3>
                <div className={styles.primaryContactCard}>  {/* NEW card */}
                    <p><strong>Name:</strong> {primaryContactFullName || 'N/A'}</p>
                    <p><strong>Phone:</strong> {primaryContactPhoneNumber || 'N/A'}</p>
                    <p><strong>Email:</strong> {primaryContactEmail || 'N/A'}</p>
                    <p><strong>Address:</strong> {primaryContactAddress || 'N/A'}</p>
                </div>
            </div>

            {/* --- Responsible Persons Display (List Remains) --- */}
            <h3 className={styles.sectionTitle}>Responsible Persons</h3>
            {responsiblePersons.length > 0 ? (
                <div className={styles.responsiblePersonsList}>
                    {responsiblePersons.map((person, index) => (
                        <div key={index} className={styles.responsiblePersonCard}>
                            <p><strong>Name:</strong> {person.fullName || 'N/A'}</p>
                            <p><strong>Phone:</strong> {person.phoneNumber || 'N/A'}</p>
                            <p><strong>Email:</strong> {person.email || 'N/A'}</p>
                            <p><strong>Address:</strong> {person.address || 'N/A'}</p>
                            <Button
                                type="button"
                                onClick={() => handleRemoveResponsiblePerson(index)}
                                variant="outlineDanger"
                                size="sm"
                                className={styles.removePersonButton}
                                disabled={removePersonLoading}         // NEW
                            >
                                {removePersonLoading ? 'Removing…' : 'Remove'}  {/* NEW */}
                            </Button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className={styles.noPersonsMessage}>No responsible persons listed yet.</p>
            )}

            {/* --- "Edit Profile" Accordion Wrapper --- */}
            <div className={styles.editProfileAccordionContainer}>
                <h3 className={styles.sectionTitle}>Edit Profile</h3>

                {/* Panel 1: Edit Primary Contact Info */}
                <AccordionItem
                    title="Edit Primary Contact Info"
                    isOpen={activeAccordion === 'primaryContact'}
                    onToggle={() => setActiveAccordion(activeAccordion === 'primaryContact' ? null : 'primaryContact')}
                >
                    {/* Form elements for primary contact info will go here */}
                    <FormInput
                        label="Primary Contact Full Name:"
                        id="editPrimaryContactFullName"
                        name="editPrimaryContactFullName"
                        type="text"
                        value={primaryContactFullName}
                        onChange={(e) => setPrimaryContactFullName(e.target.value)}
                    />
                    <FormInput
                        label="Primary Contact Phone Number:"
                        id="editPrimaryContactPhoneNumber"
                        name="editPrimaryContactPhoneNumber"
                        type="tel"
                        value={primaryContactPhoneNumber}
                        onChange={(e) => setPrimaryContactPhoneNumber(e.target.value)}
                    />
                    <FormInput
                        label="Primary Contact Email Address:"
                        id="editPrimaryContactEmail"
                        name="editPrimaryContactEmail"
                        type="email"
                        value={primaryContactEmail}
                        onChange={(e) => setPrimaryContactEmail(e.target.value)}
                        placeholder="Enter primary contact email"
                    />
                    <FormInput
                        label="Primary Contact Address:"
                        id="editPrimaryContactAddress"
                        name="editPrimaryContactAddress"
                        type="textarea"
                        rows={3}
                        value={primaryContactAddress}
                        onChange={(e) => setPrimaryContactAddress(e.target.value)}
                    />
                    <Button
                        type="button"
                        onClick={handleSavePrimaryContactInfo} /* Will be defined in Phase 3 */
                        variant="primary"
                        className={styles.accordionSaveButton} /* Add specific styles if needed */
                        size='sm'
                    >
                        Update Primary Contact Info
                    </Button>
                </AccordionItem>

                {/* Panel 2: Add Responsible Persons */}
                <AccordionItem
                    title="Add Responsible Persons"
                    isOpen={activeAccordion === 'addResponsible'}
                    onToggle={() => setActiveAccordion(activeAccordion === 'addResponsible' ? null : 'addResponsible')}
                >
                    {/* Existing form for adding a new responsible person */}
                    <div className={styles.addResponsiblePersonForm}> {/* Retain this class for consistent styling if desired */}
                        <h4>Add New Responsible Person</h4>
                        <FormInput
                            label="Full Name:"
                            id="newRespFullName"
                            name="newRespFullName"
                            value={newResponsiblePerson.fullName}
                            onChange={(e) => setNewResponsiblePerson(prev => ({ ...prev, fullName: e.target.value }))}
                        />
                        <FormInput
                            label="Phone Number:"
                            id="newRespPhoneNumber"
                            name="newRespPhoneNumber"
                            value={newResponsiblePerson.phoneNumber}
                            onChange={(e) => setNewResponsiblePerson(prev => ({ ...prev, phoneNumber: e.target.value }))}
                        />
                        <FormInput
                            label="Email:"
                            id="newRespEmail"
                            name="newRespEmail"
                            type="email"
                            value={newResponsiblePerson.email}
                            onChange={(e) => setNewResponsiblePerson(prev => ({ ...prev, email: e.target.value }))}
                        />
                        <FormInput
                            label="Address:"
                            id="newRespAddress"
                            name="newRespAddress"
                            type="textarea"
                            rows={2}
                            value={newResponsiblePerson.address}
                            onChange={(e) => setNewResponsiblePerson(prev => ({ ...prev, address: e.target.value }))}
                        />
                        <Button
                            type="button"
                            onClick={handleAddResponsiblePerson}
                            variant="primary"
                            size="sm"
                            className={styles.accordionSaveButton}
                            disabled={addPersonLoading}           // NEW
                        >
                            {addPersonLoading ? 'Adding…' : 'Add Person'}   {/* NEW */}
                        </Button>
                    </div>
                </AccordionItem>

                {/* Panel 3: Edit Account Name and User Login Details */}
                <AccordionItem
                    title="Edit Account Name and User Login Details"
                    isOpen={activeAccordion === 'accountLogin'}
                    onToggle={() => setActiveAccordion(activeAccordion === 'accountLogin' ? null : 'accountLogin')}
                >
                    {/* Account Name */}
                    <div className={styles.settingsCard}> {/* Use existing form group styling or create new */}
                        <h4>Edit Account Name</h4>
                        <FormInput
                            label="Account Name (Church Name):"
                            id="editAccountName"
                            name="editAccountName"
                            type="text"
                            value={accountName}
                            onChange={(e) => setAccountName(e.target.value)}
                            required
                        />
                        <Button
                            type="button"
                            onClick={handleSaveAccountName} /* Will be defined in Phase 3 */
                            variant="primary"
                            className={styles.accordionSaveButton} /* Add specific styles if needed */
                            size='sm'
                        >
                            Save Account Name
                        </Button>
                    </div>


                    {/* User Login Details */}
                    {/* Change Username Form */}
                    <div className={styles.settingsCard}>
                        <h4>User Login Details</h4>
                        <FormInput
                            label="Login Username:"
                            type="text"
                            id="changeUsername"
                            name="changeUsername"
                            value={editableUsername}
                            onChange={(e) => {
                                setEditableUsername(e.target.value);
                                setUsernameError('');
                                setUsernameSuccess('');
                            }}
                            placeholder="Enter new username"
                        />
                        {usernameError && <p className={pageStyles.errorMessage}>{usernameError}</p>}
                        {usernameSuccess && <p className={pageStyles.successMessage}>{usernameSuccess}</p>}
                        <Button
                            onClick={handleUsernameSave}
                            disabled={usernameLoading || !editableUsername || (currentUser && editableUsername === currentUser.username)}
                            variant="secondary"
                            className={styles.loginDetailsButton}
                            size='sm'
                        >
                            {usernameLoading ? 'Saving Username...' : 'Save Username'}
                        </Button>
                        {(!editableUsername && currentUser?.username) && <p className={pageStyles.infoMessage}>Username cannot be empty.</p>}
                    </div>


                    {/* Change Password Form */}
                    <div className={styles.settingsCard}>
                        <h4>Change Password</h4>
                        <FormInput
                            label="Current Password"
                            type="password"
                            id="currentPassword"
                            name="currentPassword"
                            value={currentPassword}
                            onChange={(e) => {
                                setCurrentPassword(e.target.value);
                                setPasswordChangeError('');
                                setPasswordChangeSuccess('');
                            }}
                            placeholder="Enter your current password"
                        />
                        <FormInput
                            label="New Password"
                            type="password"
                            id="newPassword"
                            name="newPassword"
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setPasswordChangeError('');
                                setPasswordChangeSuccess('');
                            }}
                            placeholder="Enter new password"
                        />
                        <FormInput
                            label="Confirm New Password"
                            type="password"
                            id="confirmNewPassword"
                            name="confirmNewPassword"
                            value={confirmNewPassword}
                            onChange={(e) => {
                                setConfirmNewPassword(e.target.value);
                                setPasswordChangeError('');
                                setPasswordChangeSuccess('');
                            }}
                            placeholder="Confirm new password"
                        />
                        {passwordChangeError && <p className={pageStyles.errorMessage}>{passwordChangeError}</p>}
                        {passwordChangeSuccess && <p className={pageStyles.successMessage}>{passwordChangeSuccess}</p>}
                        <Button
                            onClick={handlePasswordSave}
                            disabled={passwordChangeLoading || !currentPassword || !newPassword || !confirmNewPassword}
                            variant="secondary"
                            className={styles.loginDetailsButton}
                            size='sm'
                        >
                            {passwordChangeLoading ? 'Changing Password...' : 'Change Password'}
                        </Button>
                    </div>
                </AccordionItem>
            </div>
        </div>
    );
};

export default AccountProfilePage;